import { greatestCommonDenominator, mod } from './Math';

// GIF delays use hundredths of a second. Preview and export share this clock.
export const MIN_FRAME_DELAY = 10;
export const MAX_TIMELINE_DURATION = 120000;
export const MAX_TIMELINE_FRAMES = 6000;
export const normalizeDelay = value => {
  const delay = Number(value);
  return Number.isFinite(delay) && delay > 0
    ? Math.max(MIN_FRAME_DELAY, Math.round(delay / 10) * 10) : MIN_FRAME_DELAY;
};

export const describeImageTiming = image => {
  const frames = image.frames || [];
  const start = mod(Number.isInteger(image.startIndex) ? image.startIndex : 0, frames.length || 1);
  const order = frames.map((_, i) => mod(start + i, frames.length));
  const delays = order.map(index => normalizeDelay(frames[index].delay));
  const duration = delays.reduce((sum, delay) => sum + delay, 0);
  // Total plays; zero means unlimited. A still has no cycle.
  const playCount = Number.isSafeInteger(image.playCount) && image.playCount > 0
    ? image.playCount : 0;
  return { order, delays, duration, playCount, isStatic: frames.length <= 1,
    endBehavior: image.endBehavior === 'hide' ? 'hide' : 'hold' };
};

const boundedLcm = (a, b) => {
  const quotient = a / greatestCommonDenominator(a, b);
  return quotient > Number.MAX_SAFE_INTEGER / b ? Infinity : quotient * b;
};
export class TimelineLimitError extends Error {}

export const parseDurationSeconds = value => {
  const ticks = Number(value) * 100;
  const rounded = Math.round(ticks);
  return Number.isFinite(ticks) && Math.abs(ticks - rounded) < 0.000001 ? rounded * 10 : NaN;
};

// Check limits before allocating output frames. An explicit duration selects a
// prefix of the animation; it never stretches the source delays.
export const planTimeline = (images = [], { durationMs, compositionMode = 'source' } = {}) => {
  const tracks = images.map(image => ({ ...describeImageTiming(image),
    ...(compositionMode === 'repeat' ? { playCount: 0 } : {}) }));
  const animated = tracks.filter(track => !track.isStatic);
  const loopDuration = animated.filter(track => track.playCount === 0)
    .reduce((duration, track) => boundedLcm(duration, track.duration), 1);
  const finiteDuration = animated.reduce((duration, track) =>
    Math.max(duration, track.playCount * track.duration), 0);
  const hasLooping = animated.some(track => track.playCount === 0);
  const truncate = compositionMode === 'truncate' && animated.length > 0;
  const contentDuration = truncate ? Math.min(...animated.map(track => track.duration))
    : !Number.isFinite(loopDuration) ? Infinity : animated.length === 0
    ? Math.max(100, ...tracks.map(track => track.duration))
    : hasLooping ? Math.max(loopDuration, Math.ceil(finiteDuration / loopDuration) * loopDuration)
      : finiteDuration;
  // A disappearing final layer needs a visible empty frame in the encoded GIF.
  const needsFinalHide = !truncate && animated.some(track => track.playCount > 0 &&
    track.endBehavior === 'hide' && track.duration * track.playCount === contentDuration);
  const naturalDuration = contentDuration + (needsFinalHide ? MIN_FRAME_DELAY : 0);
  const requested = durationMs !== undefined && durationMs !== null;
  const validRequest = !requested || (Number.isSafeInteger(durationMs) && durationMs >= 10 && durationMs % 10 === 0);
  const duration = requested ? durationMs : naturalDuration;
  const error = !validRequest ? 'Choose a duration in multiples of 10 ms.'
    : truncate && duration > naturalDuration
      ? 'Truncate stops at the shortest animation. Choose a shorter duration or switch to Repeat.'
    : duration > MAX_TIMELINE_DURATION || !Number.isFinite(duration)
      ? 'The full timeline exceeds 120 seconds. Choose a shorter output duration.' : null;
  const hasFinite = animated.some(track => track.playCount > 0);
  return { tracks, naturalDuration, duration, error, hasFinite,
    shortened: requested && duration < naturalDuration };
};

export const calculateTimeline = (images, options) => {
  if (!images?.length) return [];
  const plan = planTimeline(images, options);
  if (plan.error) throw new TimelineLimitError(plan.error);
  const { tracks, duration } = plan;
  const cursors = tracks.map(track => ({ position: 0, plays: 0,
    next: track.isStatic ? Infinity : track.delays[0] }));
  const result = [];
  let time = 0;
  while (time < duration) {
    if (result.length >= MAX_TIMELINE_FRAMES) {
      throw new TimelineLimitError('The timeline exceeds 6,000 frames. Choose a shorter output duration.');
    }
    const next = Math.min(duration, ...cursors.map(cursor => cursor.next));
    result.push({ delay: next - time,
      imageIndices: cursors.map((cursor, i) => tracks[i].order[cursor.position] ?? -1) });
    time = next;
    cursors.forEach((cursor, i) => {
      if (cursor.next !== next) return;
      const track = tracks[i];
      cursor.position++;
      if (cursor.position === track.order.length) {
        cursor.position = 0;
        cursor.plays++;
        if (track.playCount && cursor.plays >= track.playCount) {
          cursor.position = track.endBehavior === 'hide' ? -1 : track.order.length - 1;
          cursor.next = Infinity;
          return;
        }
      }
      cursor.next += track.delays[cursor.position];
    });
  }
  return result;
};

export const timelineStartTimes = timeline => {
  let elapsed = 0;
  return timeline.map(frame => {
    const start = elapsed;
    elapsed += frame.delay;
    return start;
  });
};
